import { InputNumber, Slider, Space } from "antd";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from "react-redux";
import Footer from "../../../../components/Popup/_components/Footer";
import HarvestInfo from "../../../../components/Popup/PopupOpInfo/Info/Harvest/HarvestInfo";
import { OpService } from "../../../../util/services/operation";
import { selectFactory } from "../../../../util/store/factory/constants";
import { getStore, useSelector } from "../../../../util/store/store";
import { DELIVERY_TYPE, InfoMode } from "../../../../util/varibles/constants";
import { cloneObj } from "../../../../util/varibles/global";
import { IDeliveryTypeUnit, IHarvest2, IIdName, IOperation, IOperations, IUnit } from "../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../util/varibles/message";
import { selectPlan } from "../../constants";
import { planOpActions } from "../../reducer";
import { getFactorySiteTime, getLastPlaceByTime } from "../../util";
import { getDeliveryTypes, initDeliveryType } from "../EditOp/Harvest/constants";
import styles from "./style.module.scss";

interface IUnitOpMerge extends IUnit {
    delivery_types: (IDeliveryTypeUnit & {
        max_fish_amount?: number
        max_total_weight?: number
    })[]
}

interface IDeliveryTypeProps {
    data: IUnitOpMerge['delivery_types'][0]
    avg_weight: number
    onChange(value: IUnitOpMerge['delivery_types'][0]): void
}

const FishAmount: React.FC<IDeliveryTypeProps> = ({ data, avg_weight, onChange }) => {
    const { max_total_weight = 1, max_fish_amount = 1 } = data;
    const handleChange = (value: number | null) => {
        const fish_amount = value || 0;
        const total_weight = avg_weight !== -1
            ? fish_amount * avg_weight
            : Math.round((fish_amount / Math.max(1, max_fish_amount)) * 100 * max_total_weight);
        onChange({ ...data, fish_amount, total_weight });
    }

    return <div className='flex'>
        <Slider
            className='w-full'
            min={0}
            max={max_fish_amount}
            onChange={handleChange}
            value={data.fish_amount}
        />
        <InputNumber
            min={0}
            max={max_fish_amount}
            value={data.fish_amount}
            onChange={handleChange}
        />
    </div>
}

const TotalWeight: React.FC<IDeliveryTypeProps> = ({ data, avg_weight, onChange }) => {
    const { max_total_weight = 1, max_fish_amount = 1 } = data;
    const handleChange = (value: number | null) => {
        const total_weight = (value || 0) * 1000000;
        const fish_amount = avg_weight !== -1
            ? Math.floor(total_weight / avg_weight)
            : Math.round((total_weight / Math.max(1, max_total_weight)) * 100 * max_fish_amount * 1000000);
        onChange({ ...data, fish_amount, total_weight });
    }

    const convertTotalWeight = () => {
        if (avg_weight !== -1) {
            const { fish_amount } = data;
            const total_weight = fish_amount * avg_weight;
            onChange({ ...data, fish_amount, total_weight });
        }
    }

    return <div className='flex'>
        <Slider
            className='w-full'
            min={0}
            step={0.001}
            max={max_total_weight}
            value={Math.round(data.total_weight / 1000) / 1000}
            onChange={handleChange}
        />
        <Space.Compact>
            <InputNumber
                min={0}
                max={max_total_weight}
                value={Math.round(data.total_weight / 1000) / 1000}
                onBlur={convertTotalWeight}
                onChange={handleChange}
            />
            <div className={styles.suffix}>t</div>
        </Space.Compact>
    </div>
}

const DeliveryTypes: React.FC<{ data: IUnitOpMerge['delivery_types'], avg_weight: number, onChange(value: IUnitOpMerge['delivery_types']): void }> = ({ data, avg_weight, onChange }) => {
    const handleUpdate = (index: number, value: IUnitOpMerge['delivery_types'][0]) => {
        const temp = cloneObj(data);
        temp[index] = value;
        onChange(temp);
    }

    return <div className='flex flex-col gap-1'>
        {data.map((item, index) => {
            const subProps = {
                data: item,
                avg_weight,
                onChange: (value: IUnitOpMerge['delivery_types'][0]) => handleUpdate(index, value)
            }

            return <div key={item.id} data-level="delivery_types">
                <div>{item.name}</div>
                <FishAmount {...subProps} />
                <TotalWeight {...subProps} />
            </div>
        })}
    </div>
}

// const autoPlanConfig = getSetting(state.login.user.setting, PreferenceSetting.AUTO_PLAN);

interface IProps {
    visible?: boolean
    idTarget: string
    opDrag: IOperation

    onClose?(): void
}

const PopupMerge: React.FC<IProps> = (props) => {
    const controllerMove = useRef(new AbortController());
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const { idTarget, opDrag } = props;
    const { sub_operations: rootSubDrag } = opDrag.operation;
    const operations = useSelector(selectPlan.ops);
    const opTarget = operations[idTarget];
    const { sub_operations: targetSubs, factory_id: targetFactoryId } = opTarget.operation;
    const targetDeliveryTypes = useRef((() => {
        const factories = selectFactory.data(getStore())
        const factory = factories.find(item => item.id === targetFactoryId);
        return factory ? getDeliveryTypes(factory, opTarget.vessel) : {};
    })())

    const [subDrag, setSubDrag] = useState<IUnitOpMerge[]>(rootSubDrag.map(item => ({
        ...item,
        delivery_types: (item.delivery_types || []).map(sub => ({
            ...sub,
            max_fish_amount: sub.fish_amount,
            max_total_weight: Math.round(sub.total_weight / 1000) / 1000
        }))
    })))

    useEffect(() => {
        const controller = controllerMove.current;
        return () => {
            controller.abort();
        }
    }, []);

    const handleConfirm = (units: IUnit[], sites: IIdName[]) => {
        const isUpdate = subDrag.some(item => (item.delivery_types || []).some(sub => sub.fish_amount !== sub.max_fish_amount));
        const ops = [];
        let success: any;
        if (isUpdate) {
            const { dragSubs, dragSites } = subDrag.reduce((rs: { dragSubs: IUnit[], dragSites: { [id: string]: IIdName } }, item) => {
                const { delivery_types = [], site_id = "", site_name = "" } = item;
                let value: IDeliveryTypeUnit[] = [];
                delivery_types.forEach(sub => {
                    const { max_fish_amount = 1, max_total_weight = 1, fish_amount, total_weight, ...args } = sub;
                    value.push({
                        ...args,
                        fish_amount: Math.max(max_fish_amount - fish_amount, 0),
                        total_weight: Math.max(max_total_weight * 1000000 - total_weight, 0)
                    })
                })
                value = value.filter(sub => sub.fish_amount);
                if (value.length > 0) {
                    rs.dragSubs.push({
                        ...item,
                        delivery_types: value,
                        fish_amount: value.reduce((rs: number, sub) => rs + sub.fish_amount, 0),
                        total_weight: value.reduce((rs: number, sub) => rs + sub.total_weight, 0),
                    });
                    rs.dragSites[site_id] = { id: site_id, name: site_name }
                }

                return rs;
            }, { dragSubs: [], dragSites: {} });
            ops.push({
                ...opDrag,
                operation: { ...opDrag.operation, sub_operations: dragSubs, sites: Object.keys(dragSites).map(key => dragSites[key]) },
            })
            ops.push({
                ...opTarget,
                operation: { ...opTarget.operation, sub_operations: units, sites },
            })
            success = (rs: IOperation[]) => dispatch(planOpActions.updateOps({
                update: rs.reduce((rs: IOperations, item) => {
                    rs[item.operation.id] = item;
                    return rs;
                }, {})
            }));
        } else {
            ops.push({ ...opTarget, operation: { ...opTarget.operation, sub_operations: units, sites } })
            success = (rs: IOperation[]) => dispatch(planOpActions.mergeOp({ data: rs[0], deletedId: opDrag.operation.id }))
        }

        Promise.all(ops.map(item => {
            const { id, site_id, factory_id } = item.operation;
            const time = item.activity_log[0].est_start_time;
            const { start_place_id, start_place_field } = getLastPlaceByTime(item.vessel.id, time, [id]);
            const {
                factory_time,
                factoryTime,
                site_arrival_time,
                priority
            } = getFactorySiteTime(site_id, factory_id, item.vessel.type, time);
            const body = {
                start_time: item.activity_log[0].est_start_time,
                factory_time,
                site_arrival_time,
                packing_time: factoryTime,
                priority,
                start_place_id,
                start_place_field,
                source_id: item.operation.site_id,
                destination_id: item.operation.factory_id,
                vessel_id: item.vessel.id,
                is_journey_to_site: item.operation.is_journey_to_site,
                is_cleaning: item.operation.is_cleaning,
                sub_operations: item.operation.sub_operations.map(item => {
                    const { delivery_types = [], ...subArgs } = item;

                    return {
                        ...subArgs,
                        delivery_types: delivery_types.map(sub => {
                            const { tanks, ...deliveryTypeArgs } = sub;

                            return { ...deliveryTypeArgs, tanks: [] }
                        })
                    }
                })
            }

            return OpService.harvestTanks(body, controllerMove.current)
                .then(rs => {
                    const { activity_log, ...operation } = rs;
                    return {
                        ...item,
                        operation: { ...item.operation, ...operation },
                        activity_log,
                    }
                })

        }))
            .then(rs => {
                setLoading(false);
                success(rs);
                props.onClose?.();
            })
            .catch(() => {
                setLoading(false);
            })

        setLoading(true);

    }

    const handleClose = () => {
        controllerMove.current.abort();
        props.onClose?.();
    }

    const handleUpdate = (index: number, value: IUnitOpMerge) => {
        setSubDrag(prev => {
            const temp = cloneObj(prev);
            temp[index] = value;
            return temp;
        })
    }

    const { operation_code: codeA } = opDrag.operation;
    const { operation_code: codeB } = opTarget.operation;

    const { sub_operations, sites, sub_total, total_weight } = useMemo(() => {
        let dragTotal = 0, targetTotal = 0, sub: { [id: string]: IUnit } = {}, newSites: { [id: string]: IIdName } = {};
        targetSubs.forEach(item => {
            targetTotal += item.total_weight;
            sub[item.id] = item;
            newSites[item.site_id || ""] = { id: item.site_id, name: item.site_name || "" }
        })

        const directKey = Object.keys(targetDeliveryTypes.current).find(key => targetDeliveryTypes.current[key].type === DELIVERY_TYPE.DIRECT);
        if (directKey) {
            subDrag.forEach(item => {
                const { delivery_types: currentDeliveryTypes = [], site_id = "", site_name = "" } = item;
                let { delivery_types = [] }: IUnit = cloneObj(sub[item.id] || {});
                let directIndex: number = 0;
                if (sub[item.id]) {
                    directIndex = delivery_types.findIndex(deliveryType => deliveryType.type === DELIVERY_TYPE.DIRECT);
                    if (directIndex === -1) {
                        directIndex = 0;
                        delivery_types[directIndex] = initDeliveryType(targetDeliveryTypes.current[directKey])
                    }
                } else {
                    delivery_types.push(initDeliveryType(targetDeliveryTypes.current[directKey]))
                    sub[item.id] = cloneObj({ ...item, fish_amount: 0, total_weight: 0 });
                    directIndex = 0;
                }
                currentDeliveryTypes.forEach(delivery_type => {
                    const { fish_amount, total_weight } = delivery_type;
                    dragTotal += total_weight;
                    if (fish_amount) {
                        delivery_types[directIndex].fish_amount += fish_amount;
                        delivery_types[directIndex].total_weight += total_weight;
                        delivery_types[directIndex].tanks = [];
                    }
                })
                const value = delivery_types.filter(sub => sub.fish_amount);
                if (value.length > 0) {
                    sub = {
                        ...sub,
                        [item.id]: {
                            ...sub[item.id],
                            delivery_types: value,
                            fish_amount: value.reduce((rs: number, item) => rs + item.fish_amount, 0),
                            total_weight: value.reduce((rs: number, item) => rs + item.total_weight, 0)
                        }
                    }
                    newSites[site_id] = { id: site_id, name: site_name }
                }
            })
        }

        return {
            sub_total: dragTotal,
            total_weight: dragTotal + targetTotal,
            sub_operations: Object.keys(sub).map(key => sub[key]).filter(item => item.fish_amount),
            sites: Object.keys(newSites).map(key => newSites[key])
        }
    }, [subDrag, targetSubs]);

    const { capacity } = opTarget.vessel;
    const isOver = total_weight > capacity;

    let message = '';
    if (isOver)
        message = notify[NotifyCode.E1]()
    if (sub_total === 0)
        message = 'No fish were transferred'

    const percent = Math.round(total_weight / capacity * 100);

    return <>
        <div className={styles['container-adjust']}>
            <div className='font-w4'>Adjust fish amount transferred from #{codeA} to #{codeB}</div>
            <div className={styles['table-adjust']}>
                <div className={styles['line-adjust']} data-level='header'>
                    <div>Site, unit</div>
                    <div>Delivery type</div>
                    <div className='pl-[2px]'>Fish amount</div>
                    <div className='pl-[2px]'>Total weight</div>
                </div>
                {subDrag.map((item, index) => <div className={styles['line-adjust']} key={item.id}>
                    <div className="mt-[5px]">
                        {[item.site_name, item.unit_id].join(' - ')}
                    </div>
                    <DeliveryTypes data={item.delivery_types} avg_weight={item.avg_weight} onChange={value => handleUpdate(index, { ...item, delivery_types: value })} />
                </div>)}
            </div>
        </div >

        <div className='font-w4 mb-10'>Operation information after merge</div>
        <div className={styles['table-sub-operations']}>
            <div className="mb-1">
                Capacity: {Math.round(total_weight / 1000) / 1000}t / {Math.round(capacity / 1000) / 1000}t
                ({isOver ? <>{percent}%)</>
                    : <span className={styles['error']}>{notify[NotifyCode.E1]()} {percent}%</span>})
            </div>
            <HarvestInfo operation={{ ...opTarget.operation, sub_operations, sites } as IHarvest2} infoMode={InfoMode.PLAN} />
        </div>
        <Footer
            ok={{
                text: 'Confirm',
                disabled: sub_total === 0 || isOver,
                loading,
                title: message,
                click: () => handleConfirm(sub_operations, sites)
            }}
            cancel={{ click: handleClose }}
        />
    </>
}

export default PopupMerge;
